/**
 * Copernicus EFFIS Sentinel-3 hotspots as points (WFS `s3.hs`).
 * The WMS tiles are a picture only — this is the list we can score.
 * EFFIS does not publish FRP here; we use the hit as a second-satellite look.
 */
import { detectionId } from "./csv";
import { inEuropeFireDomain } from "./geo";
import { EFFIS_GWIS_WMS } from "./sources";
import type { Detection } from "./types";

/** Platform id in D1 / the scorer. S3A and S3B stay one family. */
export const S3_SOURCE = "S3";

/**
 * Recency buckets on the live layer. 6h + 12h covers the last polar gap;
 * older FireSeason_* rows are millions of points and must stay filtered out.
 */
export const S3_WFS_RECENT_CLASSES = ["6HOURS_S", "6HOURS_B", "12HOURS_S", "12HOURS_B"] as const;

/** Wider than the FIRMS 6 h revision window — otherwise 12HOURS_* cells never attach. */
export const S3_FILTER_OVERLAP_MS = 12 * 3600 * 1000;

const S3_UA = "IGNIS/0.3 (fire map; EFFIS s3.hs WFS)";
const MAX_GEOJSON_BYTES = 6_000_000;

type S3Feature = {
  properties?: { id?: string; acq_at?: string; CLASS?: string };
  geometry?: { type?: string; coordinates?: unknown };
};

type S3FeatureCollection = { features?: S3Feature[] };

/** EFFIS `acq_at` is `YYYY-MM-DD HH:MM:SS` without a zone — treat as UTC. */
export function s3AcqAtToIso(raw: string): string | null {
  const s = raw.trim();
  const m = s.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}:\d{2})$/);
  if (!m) return null;
  return `${m[1]}T${m[2]}Z`;
}

/** GeoJSON is lon,lat. Ignore anything that is not a point. */
function featureLonLat(f: S3Feature): { lon: number; lat: number } | null {
  const c = f.geometry?.coordinates;
  if (!Array.isArray(c) || c.length < 2) return null;
  const lon = Number(c[0]);
  const lat = Number(c[1]);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
  return { lon, lat };
}

export function parseS3Geojson(json: string): Detection[] {
  let body: S3FeatureCollection;
  try {
    body = JSON.parse(json) as S3FeatureCollection;
  } catch {
    return [];
  }
  const out: Detection[] = [];
  const seen = new Set<string>();
  for (const f of body.features ?? []) {
    const acq = f.properties?.acq_at;
    const iso = acq ? s3AcqAtToIso(acq) : null;
    const ll = featureLonLat(f);
    if (!iso || !ll) continue;
    // Europe clip here so Ukraine / Russia never enter D1.
    if (!inEuropeFireDomain(ll.lat, ll.lon)) continue;
    const id = detectionId(S3_SOURCE, ll.lat, ll.lon, iso);
    if (seen.has(id)) continue;
    seen.add(id);
    const klass = f.properties?.CLASS ?? "";
    out.push({
      id,
      lat: ll.lat,
      lon: ll.lon,
      observed_at: iso,
      source: S3_SOURCE,
      satellite: S3_SOURCE,
      frp_mw: null,
      confidence: 70,
      confidence_raw: klass,
      daynight: "",
      incident_id: null,
    });
  }
  return out;
}

function s3WfsUrl(klass: string): string {
  const q = new URLSearchParams({
    SERVICE: "WFS",
    VERSION: "1.1.0",
    REQUEST: "GetFeature",
    TYPENAME: "s3.hs",
    OUTPUTFORMAT: "geojson",
    FILTER: `<Filter><PropertyIsEqualTo><PropertyName>CLASS</PropertyName><Literal>${klass}</Literal></PropertyIsEqualTo></Filter>`,
  });
  return `${EFFIS_GWIS_WMS}?${q.toString()}`;
}

async function fetchS3Class(klass: string): Promise<Detection[]> {
  const res = await fetch(s3WfsUrl(klass), {
    headers: { "user-agent": S3_UA },
  });
  if (!res.ok) throw new Error(`s3 wfs ${klass} ${res.status}`);
  const buf = await res.arrayBuffer();
  if (buf.byteLength > MAX_GEOJSON_BYTES) throw new Error(`s3 wfs ${klass} too large`);
  return parseS3Geojson(new TextDecoder().decode(buf));
}

/** Pull the live recency classes and merge. One class failing must not drop the rest. */
export async function fetchS3Hotspots(): Promise<{
  rows: Detection[];
  feed: { source: string; ok: boolean; rows: number; error?: string };
}> {
  const got = await Promise.allSettled(S3_WFS_RECENT_CLASSES.map((klass) => fetchS3Class(klass)));
  const rows: Detection[] = [];
  const seen = new Set<string>();
  const errors: string[] = [];
  for (let i = 0; i < got.length; i++) {
    const item = got[i]!;
    if (item.status === "fulfilled") {
      for (const d of item.value) {
        if (seen.has(d.id)) continue;
        seen.add(d.id);
        rows.push(d);
      }
    } else {
      const message = item.reason instanceof Error ? item.reason.message : String(item.reason);
      errors.push(`${S3_WFS_RECENT_CLASSES[i]}: ${message}`);
    }
  }
  const ok = rows.length > 0 || errors.length === 0;
  return {
    rows,
    feed: {
      source: S3_SOURCE,
      ok,
      rows: rows.length,
      error: errors.length ? errors.join("; ") : undefined,
    },
  };
}
